import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { Button } from '@wf-common/atoms'
import Form from '../form/form'
import FormIteratorItem from './form-iterator-item'

import styles from './common.module.css'

/**
 * @typedef {Object} FormIteratorProperties
 * @property {string}                               property                    to access the array in the Form model
 * @property {Object}                               [model]                     the model of the overlaying Form
 * @property {Function}                             [onModelChange]             the Form update function
 * @property {Object}                               [error]                     the Form error object
 * @property {function(Object[]): Object}           [onAddHook=()=>{}]          the method invoked before adding
 * @property {function(Object[], index): boolean}   [onRemoveHook=()=>true]     the method invoked before removing
 * @property {function(Object[]): boolean}          [canAdd=()=>true]           the method to deny / allow adding
 * @property {boolean}                              [canRemoveOnlyItem=false]   to allows the removal of the last item
 * @property {boolean}                              [disabled=false]            to disable the children
 * @property {string}                               [addButtonLabel]            to have an alternative add button text
 * @property {string}                               [className]                 to allow custom styling
 */

const propTypes = {
    addButtonLabel: PropTypes.string,
    canAdd: PropTypes.func,
    canRemoveOnlyItem: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    model: PropTypes.object,
    onAddHook: PropTypes.func,
    onModelChange: PropTypes.func,
    onRemoveHook: PropTypes.func,
    property: PropTypes.string.isRequired
}

const defaultProps = {
    addButtonLabel: 'Add',
    canAdd: () => true,
    canRemoveOnlyItem: false,
    disabled: false,
    onAddHook: Function.prototype,
    onRemoveHook: () => true
}

/**
 * An Iterator with a Form attached.
 * @constructor
 *
 * @param {FormIteratorProperties} props
 * @returns {React.ReactElement}
 */
export function FormIterator(props) {
    const { error, model, onModelChange, property } = props
    const { addButtonLabel, canAdd, canRemoveOnlyItem, children, className, disabled, onAddHook, onRemoveHook } = props

    // If there is no field with the property name in the model, create one,
    // otherwise the children cause errors while binding to fields.
    if (!model[property]) {
        model[property] = []
    }

    const disableAdd = () => {
        return disabled || !canAdd(model[property])
    }
    const onAdd = () => {
        const newElement = onAddHook(model[property]) || {}
        onModelChange({
            [property]: model[property].concat([newElement])
        })
    }
    const onRemove = index => {
        const defaultRemoval = index => model[property].slice(0, index).concat(model[property].slice(index + 1))

        if (onRemoveHook(model[property], index)) {
            const newValues = defaultRemoval(index)
            onModelChange({
                [property]: newValues
            })
        }
    }

    const composedClassName = cx(styles['iterator'], className)
    return (
        <div className={composedClassName}>
            <div className={styles['iterator-content']}>
                <Form.Partial name={property} {...{ error, model }}>
                    <FormIteratorItem
                        {...{ canClose: canRemoveOnlyItem || model[property].length > 1, disabled, onRemove }}
                    >
                        {children}
                    </FormIteratorItem>
                </Form.Partial>
            </div>
            <div className={styles['iterator-footer']}>
                <Button
                    className={cx(styles['add-button'], 't3sel-form-iterator-add-button')}
                    {...{ disabled: disableAdd() }}
                    onClick={onAdd}
                >
                    {addButtonLabel}
                </Button>
            </div>
        </div>
    )
}

FormIterator.propTypes = propTypes
FormIterator.defaultProps = defaultProps
